import {
  Component,
  ElementRef,
  HostListener,
  inject,
  type OnInit,
  ViewChild,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import * as _ from 'lodash';
import { Subscription } from 'rxjs';
import { getUserSpaces } from '../../server/services/space.service';
import { sortCaseInsensitive } from '../../server/services/utils.service';
import type { OrgSpace } from '../../server/types/org-types';
import type { DeepReadonly } from '../../server/util-type';
import { AuthUserService, type UserOrgObj } from './auth-user.service';
import { ChannelService, type ChannelTitleListItem } from './channel.service';
import { MenuService } from './menu.service';
import { MessageService } from './message.service';
import { SettingsService } from './settings.service';
import { TaskService } from './task.service';
import { UtilsService } from './utils.service';

type DisplayChannel = {
  _id: string;
  orgId: string;
  title: string;
  titleSub?: Subscription;
  unreadCount: number;
  unreadCountSub?: Subscription;
};

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit {
  private authUserService = inject(AuthUserService);
  private utilsService = inject(UtilsService);
  private router = inject(Router);
  private menuService = inject(MenuService);
  private channelService = inject(ChannelService);
  private messageService = inject(MessageService);
  public taskService = inject(TaskService);
  private settingsService = inject(SettingsService);

  @ViewChild('routerOutletParent') routerOutletParent;
  @ViewChild('mobileToolbar') mobileToolbar: ElementRef;
  screenWidth: number;
  userOrgObj?: DeepReadonly<UserOrgObj>;
  orgId?: string;
  userId?: string;
  isLoggedIn = false;
  isAdmin = false;
  isSuperAdmin = false;
  loggedInOrgNameOrAccount = 'Account';
  loggedInEmail = 'test@gmail.com';
  orgLink = '';
  trialExpiryText?: string = undefined;
  spaces: DeepReadonly<OrgSpace[]> = []; // The spaces that the user is part of
  channels: DisplayChannel[] = [];

  desktopSideNavState: 'narrow' | 'wide' = 'narrow';
  showDesktopSideNavToggle = true;
  selectedDesktopSideNavState: 'narrow' | 'wide' = 'wide';

  isIosDevice = false;

  // Scroll position maintainer
  private lastPoppedScrollTop: number;
  private currentRouteId: number;
  private yScrollStack: number[] = [];

  ngOnInit() {
    // Start the authUserService
    this.authUserService.authInit();

    // Load the theme
    this.settingsService.themeObservable.subscribe((theme) => {
      const body = document.getElementsByTagName('body')[0];
      if (theme === 'dark-theme') {
        if (body.classList.contains('global-light-theme'))
          body.classList.remove('global-light-theme');
        body.classList.add('global-dark-theme');
      } else {
        if (body.classList.contains('global-dark-theme'))
          body.classList.remove('global-dark-theme');
        body.classList.add('global-light-theme');
      }
    });

    // Set side bar mode
    this.screenWidth = window.innerWidth;
    this.setSideBarStateBasedOnWidth();

    // Replace with logged in NavLinks
    this.authUserService.account.subscribe((account) => {
      this.isLoggedIn = !!account;

      // Account menu display
      this.loggedInEmail = account?.email ?? '';
    });

    let channelSub: Subscription;

    this.authUserService.userOrgObj.subscribe((userOrgObj) => {
      this.userOrgObj = userOrgObj;
      if (userOrgObj) {
        const orgId = userOrgObj.org._id;
        this.orgId = orgId;
        this.userId = userOrgObj.user._id;

        this.isAdmin = userOrgObj.user.admin;
        this.isSuperAdmin = !!userOrgObj.superAdmins;

        this.orgLink = '/org/' + orgId;

        this.spaces = getUserSpaces(userOrgObj.org.spaces, userOrgObj.user._id);
        this.spaces.forEach(({ _id: spaceId }) => {
          this.taskService.refreshTaskData({ orgId, spaceId });
        });

        if (!channelSub)
          channelSub = this.channelService.channelList.subscribe((c) =>
            this.handleChannels(c)
          );

        const sub = userOrgObj.org.subscription;
        if (sub && sub.status === 'trial') {
          this.trialExpiryText =
            sub.endAt > Date.now()
              ? dayjs(sub.endAt).toNow(true) + ' left'
              : 'expired';
        } else {
          this.trialExpiryText = undefined;
        }

        // Account menu display
        this.loggedInOrgNameOrAccount = userOrgObj.org.name;
      } else {
        this.isAdmin = false;
        this.isSuperAdmin = false;
        this.orgLink = '';
        this.spaces = [];
        this.trialExpiryText = undefined;

        // Account menu display
        this.loggedInOrgNameOrAccount = 'Account';
      }
    });

    // Check if iOS
    if (this.settingsService.getMobileOperatingSystem() === 'iOS') {
      this.isIosDevice = true;
    }

    // Check if mobile device (to show rollover buttons)
    if (this.utilsService.isMobileDevice) {
      const body = document.getElementsByTagName('body')[0];
      body.classList.add('is-mobile-device');
    }
    // Preserve the scroll position on pages between navigation
    this.router.events.subscribe((ev: any) => {
      if (ev instanceof NavigationStart) {
        if (this.routerOutletParent.nativeElement) {
          // this has been placed here as a hack since this element is not ready on first load
          const el = this.routerOutletParent.nativeElement;
          this.yScrollStack[this.currentRouteId] = el.scrollTop;
          // Determine if we are going back
          if (
            ev.restoredState &&
            ev.restoredState.navigationId &&
            this.yScrollStack[ev.restoredState.navigationId]
          ) {
            this.lastPoppedScrollTop =
              this.yScrollStack[ev.restoredState.navigationId];
          } else {
            this.lastPoppedScrollTop = 0;
          }
        }
      } else if (ev instanceof NavigationEnd) {
        if (this.routerOutletParent.nativeElement) {
          // this has been placed here as a hack since this element is not ready on first load
          this.currentRouteId = ev.id;
          const el = this.routerOutletParent.nativeElement;
          el.scrollTop = this.lastPoppedScrollTop
            ? this.lastPoppedScrollTop
            : 0;
        }
      }
    });

    this.menuService.mobileToolbarObservable.subscribe((showMenu) => {
      if (showMenu) {
        this.mobileToolbar.nativeElement.classList.remove(
          'hide-mobile-toolbar'
        );
      } else {
        this.mobileToolbar.nativeElement.classList.add('hide-mobile-toolbar');
      }
    });
  }

  @HostListener('window:resize', ['$event']) onResize(event) {
    this.screenWidth = event.target.innerWidth;
    this.setSideBarStateBasedOnWidth();
  }

  desktopSideNavToggle() {
    this.desktopSideNavState =
      this.desktopSideNavState === 'wide' ? 'narrow' : 'wide';
    this.selectedDesktopSideNavState = this.desktopSideNavState;
  }

  setSideBarStateBasedOnWidth() {
    if (this.screenWidth < 1350) {
      this.showDesktopSideNavToggle = false;
      this.desktopSideNavState = 'narrow';
    } else {
      this.showDesktopSideNavToggle = true;
      this.desktopSideNavState = this.selectedDesktopSideNavState;
    }
  }

  backNavigation() {
    window.history.back();
  }

  private handleChannels(channels: ChannelTitleListItem[]) {
    const userIds = _.uniq(channels.map((c) => c.userId));
    if (userIds.length > 1) throw new Error('Too many userIds in channels');

    const userId = userIds[0];
    if (userId !== this.userId) return;
    if (!this.orgId) throw new Error('orgId not defined');
    const orgId = this.orgId;

    channels.forEach((channel) => {
      const { _id, title } = channel;
      const existing = this.channels.find((c) => c._id === _id);
      if (existing) return (existing.title = title);

      // Create new
      const entry: DisplayChannel = { _id, orgId, title, unreadCount: 0 };

      const threadItem = this.messageService.getThreadItem({
        orgId,
        refId: _id,
        mode: channel.mode,
      });

      entry.titleSub = threadItem.titleReplayObservable.subscribe((t) => {
        entry.title = t;
        this.sortChannels();
      });

      entry.unreadCountSub =
        threadItem.unreadMessageCountReplayObservable.subscribe((count) => {
          entry.unreadCount = count;
        });

      this.channels.push(entry);
    });

    // Find removed channels
    this.channels = this.channels.filter((channel) => {
      const found = channels.find((c) => c._id === channel._id);
      if (found) return true;
      // Otherwise clean up
      channel.titleSub?.unsubscribe();
      channel.unreadCountSub?.unsubscribe();
      return false;
    });

    this.sortChannels();
  }

  private sortChannels() {
    this.channels = this.channels.sort((a, b) =>
      sortCaseInsensitive(a.title, b.title)
    );
  }
}
